// useLogin.ts
import { useNavigate } from "react-router-dom";

import backendClient from "src/api";
import { type MfaToken, type Token, type UserAuthSettings } from "src/client";
import { MfaStorageKey } from "src/experiences/auth/constants";
import { useAuthRedirect } from "src/experiences/auth/hooks/redirect";
import useUser from "src/hooks/useUser";

export const usePassLogin = () => {
  const { logUserIn } = useUser();
  const navigate = useNavigate();
  const { redirectAfterLogin } = useAuthRedirect();

  return (
    passwordString: string,
    email: string,
    authSettings: UserAuthSettings | null,
    handleApiError: (error: string) => void
  ) => {
    backendClient.auth
      .authenticatePassword({
        passwordString,
        email,
      })
      .then((tokenPayload) => {
        if (authSettings?.mfaMethods && authSettings.mfaMethods?.length > 0) {
          // If MFA is enabled, we expect a token string for MFA verification
          const mfaToken = tokenPayload as MfaToken;
          sessionStorage.setItem(MfaStorageKey, mfaToken.token);

          const configuredMethods = mfaToken.configuredMfaMethods || [];
          const availableMethods = authSettings.mfaMethods;

          // Check if user needs to setup any MFA method
          // If EMAIL is available, they can always use that (no setup needed)
          // Otherwise, check if they have at least one method configured
          const hasEmailFallback = availableMethods.includes("EMAIL");
          const hasConfiguredMethod = configuredMethods.length > 0;

          if (!hasEmailFallback && !hasConfiguredMethod) {
            // No EMAIL fallback and no configured methods - force setup
            navigate("/mfa-setup", {
              state: {
                email,
                availableMfaMethods: availableMethods,
              },
            });
            return;
          }

          // User has at least one way to verify (EMAIL or configured method)
          navigate("/mfa-verify", {
            state: {
              email,
              availableMfaMethods: availableMethods,
              configuredMethods,
            },
          });
        } else {
          const loginToken = tokenPayload as Token;
          // If MFA is not enabled, we expect a full token for login
          logUserIn(loginToken);
          redirectAfterLogin();
        }
      })
      .catch(handleApiError);
  };
};
